/**
 * Presents context-sensitive Vault navigation, creation, source, and view actions.
 *
 * The toolbar reflects catalog versus folder state and current administrative
 * capability without becoming an authorization layer. Central action grouping
 * prevents connected-source and stored-item workflows from appearing equivalent.
 */
import { CircleHelp, Download, FilePlus2, FolderPlus, LayoutGrid, List, Plus, RefreshCw, Search, Share2, StickyNote, Trash2, Upload } from 'lucide-react'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
  cn,
} from '@/components/ui'
import type { VaultLayout } from '../types'

export type VaultTab = 'files' | 'notes' | 'shared-with-me' | 'trash'

interface VaultToolbarProps {
  title: string
  description: string
  search: string
  tab: VaultTab
  showFileActions: boolean
  showNoteAction: boolean
  layout: VaultLayout
  selectedCount: number
  preparingDownload: boolean
  refreshing: boolean
  onSearchChange: (value: string) => void
  onTabChange: (tab: VaultTab) => void
  onUpload: () => void
  onNewFolder: () => void
  onNewNote: () => void
  onShowHelp: () => void
  onLayoutChange: (layout: VaultLayout) => void
  onDownloadSelection: () => void
  onRefresh: () => void
}

export function VaultToolbar({
  title,
  description,
  search,
  tab,
  showFileActions,
  showNoteAction,
  layout,
  selectedCount,
  preparingDownload,
  refreshing,
  onSearchChange,
  onTabChange,
  onUpload,
  onNewFolder,
  onNewNote,
  onShowHelp,
  onLayoutChange,
  onDownloadSelection,
  onRefresh,
}: VaultToolbarProps) {
  return (
    <>
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <div className="relative min-w-0 flex-1 sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search Vault"
              aria-label="Search Vault"
              className="pl-9"
            />
          </div>
          <Button size="icon" variant="outline" aria-label="Vault help" onClick={onShowHelp}>
            <CircleHelp className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={(value) => onTabChange(value as VaultTab)}>
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="files">Files</TabsTrigger>
            <TabsTrigger value="notes"><StickyNote className="mr-1.5 h-3.5 w-3.5" /> My notes</TabsTrigger>
            <TabsTrigger value="shared-with-me"><Share2 className="mr-1.5 h-3.5 w-3.5" /> Shared with me</TabsTrigger>
            <TabsTrigger value="trash"><Trash2 className="mr-1.5 h-3.5 w-3.5" /> Trash</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-center gap-2">
          {selectedCount > 0 && (
            <Button size="sm" variant="outline" disabled={preparingDownload} onClick={onDownloadSelection}>
              <Download className="mr-2 h-4 w-4" />
              {preparingDownload ? 'Preparing ZIP…' : `Download ZIP (${selectedCount})`}
            </Button>
          )}
          <div className="flex gap-0.5 rounded-lg bg-muted p-0.5" role="group" aria-label="Vault layout">
            <Button
              size="icon"
              variant="ghost"
              aria-label="List view"
              aria-pressed={layout === 'list'}
              className={cn(
                'h-7 w-7 transition-all duration-150',
                layout === 'list'
                  ? 'bg-background text-foreground shadow-sm hover:bg-background'
                  : 'text-muted-foreground hover:bg-transparent hover:text-foreground',
              )}
              onClick={() => onLayoutChange('list')}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label="Grid view"
              aria-pressed={layout === 'grid'}
              className={cn(
                'h-7 w-7 transition-all duration-150',
                layout === 'grid'
                  ? 'bg-background text-foreground shadow-sm hover:bg-background'
                  : 'text-muted-foreground hover:bg-transparent hover:text-foreground',
              )}
              onClick={() => onLayoutChange('grid')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
          <Button
            size="icon"
            variant="outline"
            aria-label={refreshing ? 'Refreshing Vault' : 'Refresh Vault'}
            disabled={refreshing}
            onClick={onRefresh}
          >
            <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
          </Button>
          {showFileActions && (
            <>
              <Button size="sm" onClick={onUpload}>
                <Upload className="mr-2 h-4 w-4" /> Upload
              </Button>
            </>
          )}
          {showFileActions && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline">
                    <Plus className="mr-2 h-4 w-4" /> New
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onNewFolder}>
                    <FolderPlus className="mr-2 h-4 w-4" /> Folder
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
          {showNoteAction && (
            <Button size="sm" onClick={onNewNote}>
              <FilePlus2 className="mr-2 h-4 w-4" /> New note
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
